import React, {FC} from "react";
import {
   GlobalLayout,
   PanelLayout,
   StyledDiv,
   StyledOutlinedInput,
   StyledTypography
} from "../../providers/layout/LayoutProvider";
import {makeStyles} from "@mui/styles";
import {useConsumerSendMailMutation} from "../../Queries";
import {isValidEmail} from "../../components/Validations";
import clsx from "clsx";
import {Button, darken, styled, Theme} from "@mui/material";
import {useRecoilValue} from "recoil";
import {LocalizationsAtom} from "../../providers/localization/LocalizationProvider";

export const WheatPage: FC<any> = () => {
   const localizations = useRecoilValue(LocalizationsAtom);
   const classes = useStyles({
      leftImage: localizations.frontPageImage1 || "url('https://cdn.create2stay.com/brandstatic/wheat-left.jpeg')",
      rightImage: localizations.frontPageImage2 || "url('https://cdn.create2stay.com/brandstatic/wheat-right.jpeg')",
   });

   return <div>
      <StyledDiv type={"main"} className={classes.container}>
         <StyledDiv type={"main"} className={clsx(classes.image, classes.leftImage)}>
            &nbsp;
         </StyledDiv>
         <StyledDiv type={"contrast"} className={classes.main}>
            <div className={classes.content}>
               <StyledTypography variant={"h1"} type={"main"} style={{marginBottom: '26px'}}>
                  {localizations.loginPageTitle}
               </StyledTypography>
               <StyledTypography variant={"body1"} type={"main"} style={{marginBottom: '20px'}}>
                  {localizations.loginPageDescription}<br/>
                  {localizations.loginPageDescription2}
               </StyledTypography>
               <div className={classes.accessForm}>
                  <AccessForm/>
               </div>
            </div>
         </StyledDiv>
         <StyledDiv type={"main"} className={clsx(classes.image, classes.rightImage)}>
            &nbsp;
         </StyledDiv>
      </StyledDiv>
      <WheatEasyBox/>
   </div>
}

type imageProps = {
   leftImage: string
   rightImage: string
}

const useStyles = makeStyles({
   backContainer: {
      marginTop: '20px'
   },
   content: {
      maxWidth: '500px',
      textAlign: 'center',
      padding: '20px'
   },
   container: {
      display: 'flex',
      width: '100%',
      flexDirection: 'row',
      alignItems: 'top',
      padding: '0 40px 0 40px'
   },
   accessForm: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '20px'
   },
   image: {
      width: '400px',
      minHeight: '496px',
      backgroundRepeat: "no-repeat",
      backgroundSize: "cover",
      backgroundPosition: "top center",
      overflowX: 'hidden',
      overflowY: 'hidden',
   },
   main: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      flexGrow: 1,
   },
   leftImage: {
      backgroundImage: (props: imageProps) => props.leftImage
   },
   rightImage: {
      backgroundImage: (props: imageProps) => props.rightImage
   },
   '@media only screen and (max-width: 800px)': {
      container: {
         flexDirection: 'column',
         padding: '0 23px 0 23px'
      } as any,
      image: {
         width: '100%',
         maxHeight: '200px',
         minHeight: '300px',
         backgroundPosition: "center",
      }
   },
   '@media only screen and (max-width: 1024px)': {
      container: {
         padding: '0 23px 0 23px'
      } as any
   },
})

export const AccessForm = () => {
   const localizations = useRecoilValue(LocalizationsAtom);
   const [email, setEmail] = React.useState<string>('');
   const [sent, setSent] = React.useState<boolean>(false);

   const classes = useAccessFormStyles();
   const [sendMail] = useConsumerSendMailMutation();

   const onChange = (event: any) => {
      setEmail(event.target.value);
      setSent(false);
   }

   const onSend = async () => {
      let shopId = window.shopId || window.brandId;
      if (!!email && isValidEmail(email)) {
         await sendMail({
            variables: {
               shopId: shopId,
               mail: email
            }
         });
         setEmail('');
         setSent(true);
      }
   }

   return <>
      <form className={classes.lightBoxInputArea} onSubmit={async (e) => {
         e.preventDefault();
         await onSend();
      }}>
         <StyledOutlinedInput
            className={classes.input}
            id="email"
            type="text"
            value={email}
            onChange={onChange}
            placeholder={localizations.mainPageEnterEmail}
            aria-haspopup={false}
            autoComplete={"off"}
            endAdornment={
               <WheatButton styletype={"contrast"} variant={"text"} className={classes.button} onClick={onSend}
                            disabled={!email}>
                  {localizations.mainPageSend}
               </WheatButton>
            }
         />
      </form>
      {sent && <div className={classes.emailSent}>
         <StyledTypography variant={"body1"} className={classes.emailSentDivider}
                           type={"main"}>{localizations.mainPageMailSent}</StyledTypography>
         <StyledTypography variant={"body1"} type={"main"}>{localizations.mainPageMailSent2}</StyledTypography>
      </div>}
   </>
}


const useAccessFormStyles = makeStyles({
   input: {
      width: '100%',
      height: '45px',
      borderRadius: '20px',
      minWidth: '300px'
   },
   button: {
      height: '45px'
   },
   lightBoxInputArea: {
      display: 'flex',
      alignItems: 'center'
   },
   lightBoxMaxWidth: {
      maxWidth: '60%',
      margin: '0 auto'
   },
   emailSent: {
      marginTop: '20px',
      textAlign: 'center',
      padding: '20px'
   },
   emailSentDivider: {
      marginBottom: '15px'
   },
});


type StyledButtonProps = {
   styletype: 'main' | 'contrast'
}

export const WheatButton = styled(Button)<StyledButtonProps>(({theme, variant, styletype}) => {
   const panelLayout = useRecoilValue(PanelLayout(styletype));
   const globalLayout = useRecoilValue(GlobalLayout);

   const button = panelLayout?.mainButton;

   return {
      color: '#56352C' || button?.textColor || undefined,
      '&:hover': {
         color: '#56352C' || button?.textColor || undefined,
         border: button?.border || undefined,
         backgroundColor: 'transparent'
      },
      '&:disabled': {
         color: button?.disabledTextColor || darken(button?.textColor || '#000000', 0.5) + ' !important',
      },
      fontSize: button?.fontSize || globalLayout.text?.fontSize || undefined,
      textTransform: 'none',
      borderRadius: '0',
      fontWeight: 400 || button?.fontWeight || globalLayout.text?.fontWeight || 'bold',
      fontFamily: button?.fontFamily || globalLayout.text?.fontFamily || undefined,
      border: button?.border || undefined,
      textDecoration: 'underline'
   }
});


const WheatEasyBox = () => {
   const localizations = useRecoilValue(LocalizationsAtom);
   const classes = useEasyStyles();
   return <div className={classes.container}>
      <div>
         <StyledTypography variant={"h1"} className={classes.title}>
            {localizations.easyBoxTitle}
         </StyledTypography>
      </div>
      <div>
         <div className={classes.list}>
            <div className={classes.listItem}>
               <div className={classes.listAvatar}>
                  <img src={"https://cdn.create2stay.com/brandstatic/valgafstyles.svg"} className={classes.image} alt={localizations.easyBoxSection1Title}/>
               </div>
               <div className={classes.listText}>
                  <StyledTypography variant={"h2"} className={classes.text}>
                     {localizations.easyBoxSection1Title}
                  </StyledTypography>
                  <StyledTypography variant={"body1"}>
                     {localizations.easyBoxSection1Description}
                  </StyledTypography>
               </div>
            </div>
            <div className={classes.listItem}>
               <div className={classes.listAvatar}>
                  <img src={"https://cdn.create2stay.com/brandstatic/pakstyles.svg"} className={classes.image} alt={localizations.easyBoxSection2Title}/>
               </div>
               <div className={classes.listText}>
                  <StyledTypography variant={"h2"} className={classes.text}>
                     {localizations.easyBoxSection2Title}
                  </StyledTypography>
                  <StyledTypography variant={"body1"}>
                     {localizations.easyBoxSection2Description}
                  </StyledTypography>
               </div>
            </div>
            <div className={classes.listItem}>
               <div className={classes.listAvatar}>
                  <img src={"https://cdn.create2stay.com/brandstatic/sendretur.svg"} className={classes.image} alt={localizations.easyBoxSection3Title}/>
               </div>
               <div className={classes.listText}>
                  <StyledTypography variant={"h2"} className={classes.text}>
                     {localizations.easyBoxSection3Title}
                  </StyledTypography>
                  <StyledTypography variant={"body1"}>
                     {localizations.easyBoxSection3Description}
                  </StyledTypography>
               </div>
            </div>
         </div>
      </div>
   </div>
}


const useEasyStyles = makeStyles((theme: Theme) => ({
      container: {
         display: 'flex',
         flexDirection: 'column',
         alignItems: 'center'
      },
      title: {
         marginTop: '40px'
      },
      text: {
         marginBottom: '15px'
      },
      list: {
         display: 'flex',
         flexDirection: 'row',
         justifyContent: 'center',
         marginTop: '40px'
      },
      listItem: {
         display: 'flex',
         flexDirection: 'column',
         alignItems: 'center',
         marginBottom: '20px',
         gap: '10px'
      },
      listAvatar: {},
      listText: {
         marginTop: '0',
         textAlign: 'center',
         maxWidth: '400px',
         padding: '0 20px 0 20px'
      },
      image: {
         width: '100px'
      },
      '@media only screen and (max-width: 800px)': {
         list: {
            flexDirection: 'column',
         },
         listItem: {
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            maxWidth: '600px',
            gap: '10px'
         },
      }
   }),
);
